import type { EngineParticipant, EngineResult, GeneratedMatch } from './types';
import type { GroupConfig, StandingRow } from '@/data/types';
import { generateRoundRobin } from './round-robin';
import { generateSingleElim } from './single-elim';

/**
 * Распределяет участников по группам "змейкой": сильнейшие — в разные группы.
 * Пример: 12 игроков, 3 группы.
 * P1→G1, P2→G2, P3→G3, P4→G3, P5→G2, P6→G1, P7→G1, P8→G2, P9→G3, ...
 */
function distributeSnake(
  participants: EngineParticipant[],
  groupCount: number
): EngineParticipant[][] {
  const sorted = [...participants].sort((a, b) => {
    const sa = a.seed ?? Number.POSITIVE_INFINITY;
    const sb = b.seed ?? Number.POSITIVE_INFINITY;
    return sa - sb;
  });
  const groups: EngineParticipant[][] = Array.from({ length: groupCount }, () => []);
  let direction = 1; // 1 = forward, -1 = backward
  let g = 0;
  for (const p of sorted) {
    groups[g].push(p);
    g += direction;
    if (g === groupCount) {
      g = groupCount - 1;
      direction = -1;
    } else if (g < 0) {
      g = 0;
      direction = 1;
    }
  }
  return groups;
}

export function generateGroupsPlayoff(
  participants: EngineParticipant[],
  config: GroupConfig
): EngineResult {
  if (participants.length < config.groupSize) {
    throw new Error(`Нужно минимум ${config.groupSize} участников`);
  }

  const n = participants.length;
  const groupCount = Math.max(2, Math.round(n / config.groupSize));
  const groups = distributeSnake(participants, groupCount);

  // RR требует минимум 2 участника в группе. При малом N распределение
  // может дать группу из 1 игрока — это вырожденная конфигурация.
  if (groups.some((g) => g.length < 2)) {
    throw new Error('Слишком мало участников для такой конфигурации групп');
  }

  const groupDescriptors = groups.map((arr, idx) => ({
    id: `G${idx + 1}`,
    participantIds: arr.map((p) => p.id),
  }));

  const allMatches: GeneratedMatch[] = [];
  for (const [idx, group] of groups.entries()) {
    const gid = `G${idx + 1}`;
    const rr = generateRoundRobin(group);
    for (const m of rr.matches) {
      allMatches.push({
        ...m,
        id: `GP-${gid}-${m.id}`,
        stage: 'GROUP',
        groupId: gid,
      });
    }
  }

  return {
    matches: allMatches,
    rounds: 1, // round-номера внутри RR — внутригрупповые
    groups: groupDescriptors,
  };
}

/**
 * После завершения всех групповых матчей — сгенерировать плей-офф.
 * Расстановка: A1 vs B2, B1 vs A2 для 2 групп; для 4 групп — A1 vs B2, C1 vs D2, B1 vs A2, D1 vs C2 и т.д.
 * Берём топ-`advancePerGroup` из standings каждой группы и применяем seed-order: даём им
 * последовательные seed-номера в порядке: все 1-е места по индексу группы, затем все 2-е и т.д.,
 * с перестановкой 2-х мест "перекрёстно".
 */
export function generatePlayoffFromGroups(
  groups: { id: string; participantIds: string[] }[],
  standings: StandingRow[],
  advancePerGroup: number
): EngineResult {
  // standings отсортированы общим списком; нам нужны топы по каждой группе.
  // Считаем, что standings уже содержит groupId для каждого ряда.
  const byGroup = new Map<string, StandingRow[]>();
  for (const s of standings) {
    if (!s.groupId) continue;
    if (!byGroup.has(s.groupId)) byGroup.set(s.groupId, []);
    byGroup.get(s.groupId)!.push(s);
  }

  // Топ-K в каждой группе (standings внутри группы предполагается отсортированным — но
  // защищаемся: пересортируем). Тай-брейк детерминирован: points → setDiff →
  // participantId.localeCompare (тот же третий критерий, что и в computeStandings),
  // чтобы граница выхода в плей-офф была воспроизводима.
  const topsByGroup = new Map<string, string[]>();
  for (const [gid, rows] of byGroup.entries()) {
    rows.sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      if (b.setDiff !== a.setDiff) return b.setDiff - a.setDiff;
      return a.participantId.localeCompare(b.participantId);
    });
    topsByGroup.set(gid, rows.slice(0, advancePerGroup).map((r) => r.participantId));
  }

  // Расстановка по seed: все первые места группами по порядку получают верхние
  // seed-номера, затем все вторые места и т.д. generateSingleElim применяет
  // стандартный seed-order, который разводит верхние seed-ы по разным веткам и
  // даёт "перекрёстную" расстановку (A1 vs B2, B1 vs A2 для 2 групп).
  const ordered: string[] = [];
  for (let rank = 0; rank < advancePerGroup; rank++) {
    for (const g of groups) {
      const ids = topsByGroup.get(g.id);
      if (!ids || !ids[rank]) continue;
      ordered.push(ids[rank]);
    }
  }

  // Передаём в generateSingleElim с проставленными seed по порядку
  const seeded = ordered.map((id, idx) => ({ id, seed: idx + 1 }));
  const se = generateSingleElim(seeded);

  // Карта participantId → groupId для проверки одногруппников.
  const groupOf = new Map<string, string>();
  for (const g of groups) {
    for (const pid of g.participantIds) groupOf.set(pid, g.id);
  }

  // Стандартный seed-order для некоторых конфигураций (например 3 группы × топ-2)
  // сводит топов одной группы в одном R1-матче. Разводим их свапами слотов R1.
  resolveSameGroupR1(se.matches, groupOf);

  // Подсчёт оставшихся неразрешённых конфликтов: R1-матчи с двумя реальными
  // игроками одной группы. В редких конфигурациях эвристика не может развести.
  const sameGroupR1Conflicts = se.matches.filter(
    (m) =>
      m.round === 1 &&
      m.playerAId !== null &&
      m.playerBId !== null &&
      groupOf.get(m.playerAId) !== undefined &&
      groupOf.get(m.playerAId) === groupOf.get(m.playerBId)
  ).length;

  // Переименовать stage в 'PLAYOFF' и id в `GP-P-...`
  const playoffMatches = se.matches.map((m) => ({
    ...m,
    id: `GP-P-${m.id.replace(/^SE-/, '')}`,
    stage: 'PLAYOFF' as const,
    nextMatchId: m.nextMatchId ? `GP-P-${m.nextMatchId.replace(/^SE-/, '')}` : undefined,
  }));

  return { matches: playoffMatches, rounds: se.rounds, sameGroupR1Conflicts };
}

/**
 * Эвристика: устраняет R1-матчи плей-офф, где оба игрока из одной группы.
 * Свапает игроков между R1-матчами, меняя только слоты playerAId/playerBId
 * (структура сетки — nextMatchId/nextMatchSlot — не трогается).
 *
 * Свапаем только среди матчей с двумя реальными игроками: bye-матчи (один игрок)
 * не участвуют в свапах, чтобы не пересчитывать autoAdvanceWinnerId и не ломать
 * прокинутых в R2 bye-победителей. Bye-матч одногруппников невозможен (один игрок).
 */
function resolveSameGroupR1(
  matches: GeneratedMatch[],
  groupOf: Map<string, string>
): void {
  const r1 = matches.filter((m) => m.round === 1);
  const isReal = (m: GeneratedMatch) =>
    m.playerAId !== null && m.playerBId !== null;

  const sameGroup = (m: GeneratedMatch): boolean => {
    if (!isReal(m)) return false;
    const ga = groupOf.get(m.playerAId!);
    const gb = groupOf.get(m.playerBId!);
    return ga !== undefined && ga === gb;
  };

  // Жадно: для каждого конфликтного матча ищем партнёра для свапа.
  // Несколько проходов, пока есть прогресс.
  let changed = true;
  let guard = 0;
  while (changed && guard < r1.length * r1.length + 1) {
    changed = false;
    guard++;
    for (const conflict of r1) {
      if (!sameGroup(conflict)) continue;
      // Пытаемся обменять playerB конфликтного матча с одним из игроков
      // другого реального матча так, чтобы оба матча стали смешанными.
      for (const other of r1) {
        if (other === conflict || !isReal(other)) continue;
        for (const otherSlot of ['A', 'B'] as const) {
          const confB = conflict.playerBId!;
          const otherId =
            otherSlot === 'A' ? other.playerAId! : other.playerBId!;
          // Симулируем свап: conflict.B ↔ other[slot]
          const newConflictA = conflict.playerAId!;
          const newConflictB = otherId;
          const newOtherA =
            otherSlot === 'A' ? confB : other.playerAId!;
          const newOtherB =
            otherSlot === 'B' ? confB : other.playerBId!;
          const conflictMixed =
            groupOf.get(newConflictA) !== groupOf.get(newConflictB);
          const otherMixed = groupOf.get(newOtherA) !== groupOf.get(newOtherB);
          if (conflictMixed && otherMixed) {
            conflict.playerBId = newConflictB;
            if (otherSlot === 'A') other.playerAId = confB;
            else other.playerBId = confB;
            changed = true;
            break;
          }
        }
        if (changed) break;
      }
      if (changed) break;
    }
  }
}
